
import React from 'react';
import { Link } from 'react-router-dom';
import { companyInfo } from '../data';
import { CheckCircle2, Award, Users, ShieldCheck, ArrowRight } from 'lucide-react';

const About = () => {
  return (
    <div className="pt-24 md:pt-40">
      {/* Hero */}
      <section className="bg-[#1b4c2e] text-white py-24 md:py-32 relative overflow-hidden">
        <div className="container mx-auto px-4 relative z-10 text-center">
          <h3 className="text-green-400 uppercase tracking-[0.2em] text-xs sm:text-sm font-bold mb-4">
            Mød din anlægsgartner
          </h3>
          <h1 className="text-4xl md:text-[2.5rem] font-light mb-6">Om Smith Have & Anlæg ApS</h1>
          <p className="text-xl text-green-100 max-w-3xl mx-auto leading-relaxed">
            Din professionelle anlægsgartner i Nordjylland. Vi bygger på kvalitet, troværdighed og et solidt håndværk.
          </p>
        </div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#245e39] rounded-full -mr-48 -mt-48 blur-3xl opacity-30" />
      </section>

      {/* Content */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-16 items-center">
            <div className="lg:w-1/2 space-y-6">
              <h2 className="text-3xl font-bold text-gray-900">Kvalitet i fokus</h2>
              <p className="text-gray-600 text-lg leading-relaxed">
                {companyInfo.description}
              </p>
              <p className="text-gray-600 text-lg leading-relaxed">
                Vi har mange års erfaring med at skabe smukke og funktionelle uderum for private boligejere. 
                Vores passion ligger i detaljen, og vi ved, at et godt forarbejde er nøglen til et resultat, 
                der holder i årtier.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed">
                Når du vælger os, får du en partner, der lytter til dine ønsker, giver professionel sparring 
                og udfører arbejdet til den aftalte tid og pris. Vi dækker hele Nordjylland med base i Pandrup.
              </p>
              <div className="pt-4">
                <Link 
                  to="/galleri" 
                  className="bg-[#1b4c2e] text-white w-[289px] sm:w-fit px-8 py-3 rounded-xl font-bold transition-all hover:bg-green-800 flex items-center justify-center gap-3 shadow-lg shadow-[#1b4c2e]/20"
                >
                  Se vores tidligere projekter
                  <ArrowRight size={18} />
                </Link>
              </div>
            </div>
            <div className="lg:w-1/2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {[
                  { icon: Award, title: "Høj Kvalitet", desc: "Vi går aldrig på kompromis med materialer eller udførelse." },
                  { icon: ShieldCheck, title: "Troværdighed", desc: "En aftale er en aftale. Vi overholder altid vores deadlines." },
                  { icon: Users, title: "Lokalt Fokus", desc: "Vi er din lokale partner i Nordjylland." },
                  { icon: CheckCircle2, title: "Fast Pris", desc: "Ingen skjulte gebyrer. Vi giver altid et klart og tydeligt tilbud." },
                ].map((item, i) => (
                  <div key={i} className="bg-gray-50 p-8 rounded-2xl border border-gray-100">
                    <item.icon size={32} className="text-green-600 mb-4" />
                    <h3 className="text-xl font-bold mb-2 text-gray-900">{item.title}</h3>
                    <p className="text-gray-600 text-sm">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Shaun Section */}
      <section className="py-24 bg-white border-t border-gray-100">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row-reverse gap-16 items-center">
            <div className="lg:w-1/2 space-y-6">
              <h3 className="text-green-600 font-bold uppercase tracking-wider text-sm">Manden bag firmaet</h3>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Mød Shaun Smith</h2>
              <div className="w-20 h-1.5 bg-green-600 rounded-full"></div>
              <p className="text-gray-600 text-lg leading-relaxed">
                Det er Shaun Smith, der driver Smith Have & Anlæg ApS til daglig. Med en dyb passion for faget og et skarpt øje for detaljen, er det Shaun, der sikrer, at hvert eneste projekt lever op til vores tårnhøje standarder.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed">
                Shaun er kendt for at levere sit arbejde til UG. For ham er en opgave ikke færdig, før alt spiller 100% – uanset om det er en mindre fliserens eller et stort nyanlæg af en have. Hans personlige engagement og faglige stolthed er fundamentet i alt, hvad vi foretager os.
              </p>
              <div className="flex items-center gap-4 p-4 bg-green-50 rounded-xl border border-green-100">
                <div className="bg-green-600 p-2 rounded-lg text-white">
                  <Award size={24} />
                </div>
                <div>
                  <p className="font-bold text-gray-900">Kvalitetsgaranti</p>
                  <p className="text-sm text-gray-600">Shaun fører personligt tilsyn med alle projekter.</p>
                </div>
              </div>
            </div>
            <div className="lg:w-1/2">
              <div className="relative">
                <div className="absolute -inset-4 bg-green-100 rounded-3xl -rotate-2"></div>
                <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-[4/5]">
                  <img 
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/8418e253-1d56-459d-9b64-299630f7d780/1-shaun.jpg" 
                    alt="Shaun Smith - Smith Have & Anlæg ApS" 
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team/Spirit section */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900">Vi skaber resultater, vi er stolte af</h2>
            <div className="relative rounded-3xl overflow-hidden shadow-2xl h-[400px]">
              <img 
                src="/kundebilleder/imageye___-_imgi_67_667357066_122134739817028357_4663739399389733083_n.jpeg" 
                alt="Arbejde udført af Smith Have & Anlæg ApS" 
                className="w-full h-full object-cover"
              />
            </div>
            <p className="text-xl text-gray-700 italic font-medium">
              "Det vigtigste for os er det smil vi ser hos kunden, når de ser deres nye terrasse eller indkørsel for første gang."
            </p>
            <div className="pt-6">
              <Link 
                to="/galleri" 
                className="inline-flex items-center gap-3 text-green-700 font-bold text-lg hover:underline"
              >
                Se hele vores galleri her
                <ArrowRight size={20} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
